import { useState, useEffect, useMemo } from "react";
import {
  Sparkles,
  ChevronUp,
  Copy,
  Check,
  AlertTriangle,
  TrendingUp,
  Clock,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";

import { HorizontalBarChart } from "./chart/horizontal-bar-chart";
import { SimpleBarChart } from "./chart/simple-bar-chart";
import { DonutChart } from "./chart/donut-chart";
import { GroupedBarChart } from "./chart/grouped-bar-chart";
import { AIChatPanel } from "./ai-chat-panel";
import AIDisclaimer from "../ai-disclaimer/ai-disclaimer";


interface AISummaryExpandedProps {
  aiSummaryData?: any;
  isLoading?: boolean;
  isError?: boolean;
  onCollapse: () => void;
}

function ApiChartsRenderer({ charts, currency = "AED" }: { charts: any[]; currency?: string }) {
  const chartMap: Record<string, any> = {
    bar: SimpleBarChart,
    horizontal_bar: HorizontalBarChart,
    donut: DonutChart,
    grouped_bar: GroupedBarChart,
  };

  return (
    <div className="ai-summary-panel__visuals">
      {charts.map((chart: any, i: number) => {
        const Component = chartMap[chart.chart_type];
        return Component ? (
          <Component key={i} chart={chart} currency={currency} />
        ) : null;
      })}
    </div>
  );
}

function useTypingEffect(text: string, speed = 18) {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));

      if (i >= text.length) {
        clearInterval(interval);
        setDone(true);
      }
    }, speed);

    return () => clearInterval(interval);
  }, [text, speed]);

  return { displayed, done };
}

export function AISummaryExpanded({ aiSummaryData, isLoading, isError, onCollapse }: AISummaryExpandedProps) {
  const dynamicData = useMemo(() => {
    if (!aiSummaryData) return { summaryText: "", insights: [], charts: [], currency: "AED" };

    const insights = (aiSummaryData.flags || []).map((flag: any) => ({
      icon:
        flag.severity === "critical"
          ? AlertTriangle
          : flag.severity === "warning"
          ? Clock
          : TrendingUp,
      text: flag.message,
      title:
        flag.severity === "critical"
          ? "Critical"
          : flag.severity === "warning"
          ? "Warning"
          : "Info",
      color:
        flag.severity === "critical"
          ? "#E85D3A"
          : flag.severity === "warning"
          ? "#D97706"
          : "#059669",
    }));

    return {
      summaryText: aiSummaryData.summary || "",
      insights,
      charts: aiSummaryData.charts || [],
      currency: aiSummaryData?.charts?.[0]?.currency || "AED",
    };
  }, [aiSummaryData]);

  const [copied, setCopied] = useState(false);
  const [insightsVisible, setInsightsVisible] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState<"up" | "down" | null>(null);
  const [chatOpen, setChatOpen] = useState(false);

  const { displayed, done } = useTypingEffect(dynamicData.summaryText, 12);

  useEffect(() => {
    if (done) {
      setTimeout(() => setInsightsVisible(true), 200);
    }
  }, [done]);

  function handleCopy() {
    navigator.clipboard.writeText(dynamicData.summaryText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="ai-summary-panel__expanded">
      <div className="ai-summary-panel__header">
        <div className="ai-summary-panel__header-left">
          <div className="ai-summary-panel__header-icon">
            <Sparkles />
          </div>
          <span className="ai-summary-panel__header-title">AI Summary</span>
          <span className="ai-summary-panel__header-badge">Beta</span>
        </div>

        <div className="ai-summary-panel__header-actions">
          <button
            onClick={handleCopy}
            className={`ai-summary-panel__header-btn ${copied ? "copied" : ""}`}
          >
            {copied ? <Check /> : <Copy />}
          </button>

          <button onClick={onCollapse} className="ai-summary-panel__header-btn">
            <ChevronUp />
          </button>
        </div>
      </div>

      <div className="ai-summary-panel__body">
        {isLoading && (
          <div className="ai-summary-panel__loading">
            <div className="ai-summary-panel__loading-spinner-large" />
            <span className="ai-summary-panel__loading-text">Generating AI insights...</span>
          </div>
        )}

        {!isLoading && isError && (
          <p className="ai-summary-panel__error">
            Unable to generate AI summary. Please try again later.
          </p>
        )}

        {!isLoading && !isError && (
          <p className="ai-summary-panel__summary">
            {displayed}
            {!done && <span className="ai-summary-panel__summary-cursor" />}
          </p>
        )}

        {dynamicData.charts.length > 0 && insightsVisible && (
          <ApiChartsRenderer charts={dynamicData.charts} currency={dynamicData.currency} />
        )}

        {insightsVisible && (
          <div className="ai-summary-panel__insights">
            {dynamicData.insights.map((insight: any, i: number) => (
              <div
                key={i}
                className={`ai-summary-panel__insight-chip ai-summary-panel__insight-chip--${insight.title?.toLowerCase() || "info"}`}
              >
                <insight.icon style={{ color: insight.color }} />
                <span className="ai-summary-panel__insight-chip-title">{insight.title}</span>
                <span className="ai-summary-panel__insight-chip-text">— {insight.text}</span>
                <svg className="ai-summary-panel__insight-chip-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </div>
            ))}
          </div>
        )}

        {insightsVisible && (
          <div className="ai-summary-panel__feedback">
            <div className="ai-summary-panel__feedback-left">
              <div className="ai-summary-panel__feedback-thumbs">
                <span className="ai-summary-panel__feedback-label">Helpful?</span>
                <button
                  onClick={() => setFeedbackGiven(feedbackGiven === "up" ? null : "up")}
                  className={`ai-summary-panel__feedback-btn${feedbackGiven === "up" ? " ai-summary-panel__feedback-btn--up" : ""}`}
                >
                  <ThumbsUp />
                </button>
                <button
                  onClick={() => setFeedbackGiven(feedbackGiven === "down" ? null : "down")}
                  className={`ai-summary-panel__feedback-btn${feedbackGiven === "down" ? " ai-summary-panel__feedback-btn--down" : ""}`}
                >
                  <ThumbsDown />
                </button>
              </div>
              <span className="ai-summary-panel__feedback-disclaimer"><AIDisclaimer sx={{ mt: 0 }} /></span>
            </div>
            {!chatOpen && (
              <button onClick={() => setChatOpen(true)} className="ai-summary-panel__ask-ai-btn">
                <Sparkles />
                <span>Ask AI</span>
              </button>
            )}
          </div>
        )}
      </div>

      {chatOpen && (
        <AIChatPanel
          sessionId={aiSummaryData?.session_id}
          onClose={() => setChatOpen(false)}
        />
      )}
    </div>
  );
}
